import api from './api';

/**
 * Get current user from localStorage
 * @returns {Object|null} User object or null
 */
const getCurrentUser = () => {
  try {
    const userStr = localStorage.getItem('auth_user');
    return userStr ? JSON.parse(userStr) : null;
  } catch (error) {
    console.error('Error parsing user from localStorage:', error);
    return null;
  }
};

/**
 * Create a new template
 * @param {Object} data - Template data (all optional for auto-save)
 * @returns {Promise}
 */
export const createTemplate = async (data) => {
  try {
    const user = getCurrentUser();
    console.log('🔄 Creating template with data:', data);
    console.log('👤 Current user:', user);

    const userId =
      data.user_id ||
      data.userId ||
      user?.id ||
      user?.user_id ||
      user?.userId ||
      null;

    const organizationId =
      data.organization_id ||
      data.organizationId ||
      user?.organization_id ||
      user?.organizationId ||
      user?.default_organization_id ||
      user?.organization?.id ||
      null;

    const createdByUserId =
      data.created_by_user_id ||
      userId ||
      null;

    const payload = {
      template_id: data.template_id,
      name: data.name || data.subject || 'Untitled Template',
      description: data.description,
      subject: data.subject,
      message: data.message,
      reminder_frequency: data.reminder_frequency,
      status: data.status || 'draft',
      document_ids: data.document_ids || [],
      workflow_type: data.workflow_type,
      recipients: data.recipients || [],
      fields: data.fields || [],
      user_id: userId,
      organization_id: organizationId,
      created_by_user_id: createdByUserId,
    };
    
    console.log('📤 Sending template-create request to /template-create with payload:', payload);
    const response = await api.post('/template-create', payload);
    console.log('✅ Template created successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('❌ Error creating template:', error);
    console.error('❌ Error response:', error.response?.data);
    console.error('❌ Error status:', error.response?.status);
    throw error;
  }
};

/**
 * Update an existing template
 * @param {number} templateId - Template ID
 * @param {Object} data - Template data (all optional for auto-save)
 * @returns {Promise}
 */
export const updateTemplate = async (templateId, data) => {
  try {
    const response = await api.put(`/template-update/${templateId}`, {
      name: data.name,
      description: data.description,
      subject: data.subject,
      message: data.message,
      reminder_frequency: data.reminder_frequency,
      status: data.status,
      document_ids: data.document_ids,
      workflow_type: data.workflow_type,
      recipients: data.recipients || [],
      fields: data.fields || [],
    });
    return response.data;
  } catch (error) {
    console.error('Error updating template:', error);
    throw error;
  }
};

/**
 * Get template by ID
 * @param {number} templateId - Template ID
 * @returns {Promise}
 */
export const getTemplate = async (templateId) => {
  try {
    const response = await api.get(`/template-show/${templateId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching template:', error);
    throw error;
  }
};

/**
 * List templates
 * @param {Object} params - Query parameters (status, etc.)
 * @returns {Promise}
 */
export const listTemplates = async (params = {}) => {
  try {
    const response = await api.get('/template-list', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching templates:', error);
    throw error;
  }
};

/**
 * Delete template
 * @param {number} templateId - Template ID
 * @returns {Promise}
 */
export const deleteTemplate = async (templateId) => {
  try {
    const response = await api.delete(`/template-delete/${templateId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting template:', error);
    throw error;
  }
};

/**
 * Add document to template
 * @param {number} templateId - Template ID
 * @param {number} documentId - Document ID
 * @returns {Promise}
 */
export const addDocumentToTemplate = async (templateId, documentId) => {
  try {
    const response = await api.post(`/template-add-document/${templateId}`, {
      document_id: documentId,
    });
    return response.data;
  } catch (error) {
    console.error('Error adding document to template:', error);
    throw error;
  }
};

/**
 * Remove document from template
 * @param {number} templateId - Template ID
 * @param {number} documentId - Document ID
 * @returns {Promise}
 */
export const removeDocumentFromTemplate = async (templateId, documentId) => {
  try {
    const response = await api.post(`/template-remove-document/${templateId}`, {
      document_id: documentId,
    });
    return response.data;
  } catch (error) {
    console.error('Error removing document from template:', error);
    throw error;
  }
};

/**
 * Send envelope from template
 * @param {Object} data - send payload
 * @param {number} data.template_id - Template ID
 * @param {Array} [data.variable_signees] - Placeholder substitutions
 * @returns {Promise}
 */
export const sendEnvelopeFromTemplate = async (data) => {
  try {
    const response = await api.post('/envelope-send-from-template', data);
    return response.data;
  } catch (error) {
    console.error('Error sending envelope from template:', error);
    throw error;
  }
};

